import "./google-sign-in__large--black.scss"
import "./google-sign-in__large--white.scss"
import "./google-sign-up__large--white.scss"

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client"
const SIGNUP_POPUP_FLAG = "signupPopupOpen"

let googleObserverStarted = false
let googleScriptLoading = false
let googleScriptReady = false
const googleScriptCallbacks = new Set<() => void>()

function getGoogle() {
  return (window as any).google
}

function isSignupPopupOpen() {
  return Boolean((window as any)[SIGNUP_POPUP_FLAG])
}

function dispatchSocialAuthSuccess(provider: string, detail: Record<string, unknown> = {}) {
  const event = new CustomEvent("social-auth:success", { detail: { provider, ...detail } })
  window.dispatchEvent(event)
}

function ensureGoogleScript(callback: () => void) {
  if (googleScriptReady || getGoogle()?.accounts?.id) {
    googleScriptReady = true
    callback()
    return
  }

  googleScriptCallbacks.add(callback)

  if (googleScriptLoading) return

  googleScriptLoading = true
  const script = document.createElement("script")
  script.src = GOOGLE_SCRIPT_SRC
  script.async = true
  script.defer = true
  script.onload = () => {
    googleScriptReady = true
    googleScriptLoading = false
    googleScriptCallbacks.forEach((cb) => cb())
    googleScriptCallbacks.clear()
  }
  document.head.appendChild(script)
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

function showGoogleSignInMessage(button: HTMLElement, message: string) {
  const existing = button.parentElement?.querySelector(".google-signin-message")
  const el = (existing || document.createElement("div")) as HTMLElement

  if (!existing) {
    el.className = "google-signin-message"
    el.setAttribute("role", "status")
    el.setAttribute("aria-live", "polite")
    el.style.marginTop = "0.5rem"
    el.style.fontSize = "0.875rem"
    button.insertAdjacentElement("afterend", el)
  }

  el.textContent = message
}

function initGoogleSignInForButton(button: HTMLElement) {
  if (button.dataset.googleInit === "true") return
  button.setAttribute("data-google-init", "true")

  const clientId = button.getAttribute("data-client-id") || ""
  const nonce = button.getAttribute("data-nonce") || undefined

  if (!clientId) {
    // eslint-disable-next-line no-console
    console.error("Missing client ID for Google Sign In.")
    return
  }

  const onCredential = (response: any) => {
    const credential = response?.credential
    if (!credential) {
      // eslint-disable-next-line no-console
      console.error("Sign in with Google failed: Missing credential")
      return
    }

    fetch("/auth/google/handle_success", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ credential }),
    })
      .then(async (res) => {
        let data: any = null
        try {
          data = await res.json()
        } catch (_error) {
          data = null
        }

        if (res.ok) {
          const redirectUrl = data?.redirect_url || "/profile"
          if (isSignupPopupOpen()) {
            dispatchSocialAuthSuccess("google", { redirectUrl })
          } else {
            window.location.href = redirectUrl
          }
          return
        }

        showGoogleSignInMessage(
          button,
          data?.message || "Sign in with Google failed. Please try again.",
        )
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error sending Google sign in data to server:", error)
        showGoogleSignInMessage(button, "An error occurred during sign in. Please try again.")
      })
  }

  function initializeGsi() {
    const google = getGoogle()
    if (!google?.accounts?.id) return

    google.accounts.id.initialize({
      client_id: clientId,
      callback: onCredential,
      nonce,
      ux_mode: "popup",
      itp_support: true,
      auto_select: false,
    })

    const container = button.parentElement?.querySelector("#googleid-signin")
    if (container instanceof HTMLElement) {
      google.accounts.id.renderButton(container, { theme: "outline", size: "large" })
    }
  }

  function triggerGoogle() {
    const google = getGoogle()
    if (!google?.accounts?.id) {
      showGoogleSignInMessage(button, "Google sign-in is still loading. Please try again.")
      return
    }

    google.accounts.id.prompt((notification: any) => {
      if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
        showGoogleSignInMessage(
          button,
          "Google sign-in could not start. Please allow popups and make sure you are signed in to Google.",
        )
      }
    })
  }

  button.addEventListener("click", (event) => {
    event.preventDefault()
    triggerGoogle()
  })

  button.addEventListener("keydown", (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      triggerGoogle()
    }
  })

  ensureGoogleScript(initializeGsi)
}

function initGoogleSignIn() {
  const buttons = Array.from(document.querySelectorAll("#custom-google-signin"))
  buttons.forEach((button) => initGoogleSignInForButton(button as HTMLElement))
}

function observeGoogleButtons() {
  if (!googleObserverStarted) {
    googleObserverStarted = true
    const observer = new MutationObserver(() => {
      initGoogleSignIn()
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  initGoogleSignIn()
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", observeGoogleButtons)
} else {
  observeGoogleButtons()
}
